import React, { useState, useEffect, useRef } from 'react';
import { Input } from './ui/input';

// Autocomplétion via l'API Adresse du gouvernement français (gratuite, sans clé).
// type: undefined => adresses complètes | "municipality" => villes/communes
export const AddressAutocomplete = ({
  value,
  onChange,
  onSelect,
  type,
  placeholder,
  className,
  id,
  required,
  'data-testid': testId,
}) => {
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const boxRef = useRef(null);
  const skipNext = useRef(false);
  const focusedRef = useRef(false);

  useEffect(() => {
    if (skipNext.current) { skipNext.current = false; return; }
    const q = (value || '').trim();
    if (q.length < 3) { setSuggestions([]); setOpen(false); return; }
    const ctrl = new AbortController();
    const timer = setTimeout(async () => {
      try {
        const params = new URLSearchParams({ q, limit: '5', autocomplete: '1' });
        if (type) params.set('type', type);
        const res = await fetch(`https://api-adresse.data.gouv.fr/search/?${params.toString()}`, { signal: ctrl.signal });
        const data = await res.json();
        const feats = data.features || [];
        setSuggestions(feats);
        // N'ouvre la liste que si le champ a le focus (évite l'ouverture sur remplissage automatique)
        setOpen(focusedRef.current && feats.length > 0);
        setActive(-1);
      } catch (e) { /* réseau/abort : saisie libre en secours */ }
    }, 300);
    return () => { clearTimeout(timer); ctrl.abort(); };
  }, [value, type]);

  useEffect(() => {
    const onDoc = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const choose = (f) => {
    skipNext.current = true;
    onSelect(f.properties, f);
    setOpen(false);
    setSuggestions([]);
  };

  return (
    <div className="relative" ref={boxRef}>
      <Input
        id={id}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className={className}
        required={required}
        data-testid={testId}
        autoComplete="off"
        onFocus={() => { focusedRef.current = true; }}
        onBlur={() => { focusedRef.current = false; }}
        onKeyDown={(e) => {
          if (!open) return;
          if (e.key === 'ArrowDown') { e.preventDefault(); setActive((a) => Math.min(a + 1, suggestions.length - 1)); }
          else if (e.key === 'ArrowUp') { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)); }
          else if (e.key === 'Enter' && active >= 0) { e.preventDefault(); choose(suggestions[active]); }
          else if (e.key === 'Escape') { setOpen(false); }
        }}
      />
      {open && (
        <ul
          className="absolute z-50 mt-1 w-full bg-white border border-slate-200 rounded-md shadow-lg max-h-56 overflow-y-auto"
          data-testid={testId ? `${testId}-suggestions` : undefined}
        >
          {suggestions.map((f, i) => (
            <li
              key={f.properties.id || i}
              onMouseDown={(e) => { e.preventDefault(); choose(f); }}
              onMouseEnter={() => setActive(i)}
              className={`px-3 py-2 text-sm cursor-pointer ${i === active ? 'bg-[#FAF6EE] text-[#8F7344]' : 'text-slate-700 hover:bg-slate-50'}`}
            >
              {f.properties.label}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default AddressAutocomplete;
